import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { METRICS, formatStatistic, formatCount } from "./metrics.js";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    margin: theme.spacing(0, 2, 2, 2),
  },
  panel: {
    flex: "1 1 420px",
    minWidth: 320,
  },
  heading: {
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  title: {
    fontWeight: 600,
  },
  caption: {
    color: theme.palette.text.secondary,
  },
  metric: {
    fontWeight: 500,
  },
}));

function StatisticsPanel(props) {
  const { title, caption, statistics, metrics } = props;
  const classes = useStyles();

  return (
    <Paper variant="outlined" className={classes.panel}>
      <div className={classes.heading}>
        <Typography variant="subtitle2" className={classes.title}>
          {title}
        </Typography>
        <Typography variant="caption" className={classes.caption}>
          {caption}
        </Typography>
      </div>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Metric</TableCell>
            <TableCell align="right">Readings</TableCell>
            <TableCell align="right">Mean</TableCell>
            <TableCell align="right">Min</TableCell>
            <TableCell align="right">Max</TableCell>
            <TableCell align="right">Std Dev</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {metrics.map((metric) => {
            const statistic = statistics[metric.key];

            return (
              <TableRow key={metric.key}>
                <TableCell className={classes.metric}>{metric.label}</TableCell>
                <TableCell align="right">
                  {formatCount(statistic.count)}
                </TableCell>
                <TableCell align="right">
                  {formatStatistic(statistic.mean)}
                </TableCell>
                <TableCell align="right">
                  {formatStatistic(statistic.min)}
                </TableCell>
                <TableCell align="right">
                  {formatStatistic(statistic.max)}
                </TableCell>
                <TableCell align="right">
                  {formatStatistic(statistic.stdDev)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Paper>
  );
}

StatisticsPanel.propTypes = {
  title: PropTypes.string,
  caption: PropTypes.string,
  statistics: PropTypes.object,
  metrics: PropTypes.array,
};

/**
 * Both statistic sets, side by side, so the effect of the deleted readings is
 * visible rather than assumed. A metric nobody recorded in the period is left
 * out entirely instead of filling the table with empty rows.
 */
function SampleStatistics(props) {
  const { statistics, deletedCount } = props;
  const classes = useStyles();

  const recorded = METRICS.filter(
    (metric) => statistics.all[metric.key].count > 0
  );

  if (recorded.length === 0) return null;

  return (
    <div className={classes.root}>
      <StatisticsPanel
        title="Excluding deleted readings"
        caption="What the surviving measurements say"
        statistics={statistics.excludingDeleted}
        metrics={recorded}
      />
      <StatisticsPanel
        title="All readings"
        caption={
          deletedCount > 0
            ? `Includes ${formatCount(deletedCount)} deleted reading${
                deletedCount === 1 ? "" : "s"
              }`
            : "No readings were deleted in this period"
        }
        statistics={statistics.all}
        metrics={recorded}
      />
    </div>
  );
}

SampleStatistics.propTypes = {
  statistics: PropTypes.object.isRequired,
  deletedCount: PropTypes.number,
};

export default SampleStatistics;
